import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { FaShoppingBag } from "react-icons/fa";
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from 'react-i18next';

// Styled Components
const Header = tw.header`
  flex justify-between items-center max-w-screen-xl mx-auto
  mt-[40px] mb-[80px] lg:mb-[40px] px-4 sm:px-6 lg:px-8
`;

const CartIcon = styled(FaShoppingBag)`
  ${tw`text-gray-800 hover:text-[#336734] transition duration-300`};
  font-size: 18px;
  margin-left: 8px;
`;

const NavLinks = tw.div`flex items-center gap-2`;

const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-4 lg:my-0
  tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#336734] hocus:text-[#000000]
  font-sans font-medium
`;

const PrimaryLink = tw(NavLink)`
  lg:mx-0 px-6 py-2 rounded bg-[#336734] text-gray-100
  hocus:bg-[#336734] hocus:text-gray-200 focus:shadow-outline border-b-0
`;

const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0! absolute left-1/2 -translate-x-1/2 z-10`};
  img {
    width: 130px;
    height: auto;
    ${tw`mr-3`}
  }
`;

const MobileNavLinksContainer = tw.nav`
  flex flex-1 items-center justify-between lg:hidden relative
`;

const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
  absolute right-0 top-1/2 -translate-y-1/2
`;

const MobileNavLinks = motion(styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: min(80vw, 300px);
  height: 100vh;
  padding: 1.5rem;
  background-color: white;
  color: #1f2937;
  box-shadow: -5px 0 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`);

const MobileNavLinksList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  margin-top: 50px;
`;

const MobileNavLink = styled(NavLink)`
  ${tw`block w-full text-center p-0 rounded-md hover:bg-gray-100`};
`;

const MobilePrimaryLink = styled(PrimaryLink)`
  ${tw`block w-full text-center p-0`};
`;
const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 items-center justify-between relative
`;

const DropdownMenu = styled.div`
  ${tw`absolute mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5`};
  z-index: 50;
  min-width: 200px;
  padding: 0;

  right: 0;

  html[dir="rtl"] & {
    right: auto;
    left: 0;
  }
`;

const LogoutButton = styled.button`
  ${tw`px-4 py-2 text-gray-800 transition-colors duration-300 font-medium`};
  width: 100%;
  text-align: center;
  &:hover {
    background-color: #336734;
    color: white;
  }
`;

const NavLinkStyled = styled(Link)`
  ${tw`block px-4 py-2 text-gray-800 transition-colors duration-300 font-medium`};
  width: 100%;
  text-align: center;
  &:hover {
    background-color: #336734;
    color: white;
  }
`;

const UserButton = styled.button`
  ${tw`relative px-4 py-2 rounded-full bg-[#336734] text-white font-semibold mr-4 transition duration-300`};
  &:hover {
    background-color: #336734;
    color: #ffffff;
  }
  &:focus {
    ${tw`outline-none`};
  }
`;

// NEW Styled Component for mobile user button
const MobileUserButton = styled.div`
  ${tw`w-full text-center px-4 py-3 rounded-md`};
  background-color: #336734;
  color: white;
  font-weight: bold;
  pointer-events: none; // Make it non-clickable
  margin-bottom: 1rem;
`;

export default ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { showNavLinks, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];
const { i18n, t } = useTranslation();

  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    localStorage.removeItem("id");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("role");
    setUser(null);
    navigate("/");
    setDropdownOpen(false);
    if (showNavLinks) {
      toggleNavbar();
    }
  };

  const handleNavLinkClick = () => {
    setDropdownOpen(false);
    toggleNavbar();
  };

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src={logo} alt="logo" />
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;

  const defaultLinks = [
    <NavLinks key={1} tw="flex items-center gap-6">
      <NavLink href="/">{t('Acceuil')}</NavLink>
      <NavLink href="/Boutique">{t('Boutique')}</NavLink>
      <NavLink href="/à-props">{t('à_propos')}</NavLink>
      <NavLink href="/contact">{t('Contact')}</NavLink>
    </NavLinks>,

    <NavLinks key={2} tw="flex items-center gap-4">
      <NavLink href="/checkout">
        <CartIcon />
      </NavLink>
      {user ? (
        <div tw="relative inline-block">
          <UserButton onClick={() => setDropdownOpen(!dropdownOpen)}>
            {user.name}
          </UserButton>
          {dropdownOpen && (
            <DropdownMenu>
                <NavLinkStyled href="/checkout">{t('Paiement')}</NavLinkStyled>
              <NavLinkStyled href="/dashboard">{t('Tableau_de_bord')}</NavLinkStyled>
              <NavLinkStyled href="/profile">{t('Profile')}</NavLinkStyled>
              <LogoutButton onClick={handleLogout}>
                 {t('Déconnexion')}
              </LogoutButton>
            </DropdownMenu>
          )}
        </div>
      ) : (
        <>
          <NavLink href="/login">{t('Connexion')}</NavLink>
          <PrimaryLink
            css={roundedHeaderButton && tw`rounded-full`}
            href="/signup"
          >
            {t('Inscription')}
          </PrimaryLink>
        </>
      )}
    </NavLinks>,
  ];

  links = links || defaultLinks;

  return (
    <Header className={className || "header-light"}>
      {/* Desktop Navigation */}
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        <div className="flex-1 flex justify-start">{links[0]}</div>
        {logoLink}
        <div className="flex-1 flex justify-end">{links[1]}</div>
      </DesktopNavLinks>

      {/* Mobile Navigation */}
      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <div className="relative z-20">
          <NavToggle onClick={toggleNavbar}>
            {showNavLinks ? <CloseIcon tw="w-6 h-6 text-gray-800" /> : <MenuIcon tw="w-6 h-6 text-gray-800" />}
          </NavToggle>
        </div>

        <AnimatePresence>
          {showNavLinks && (
            <MobileNavLinks
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.2 }}
            >
              <NavToggle onClick={toggleNavbar} style={{ position: 'absolute', top: '20px', right: '20px', transform: 'none' }}>
                <CloseIcon tw="w-6 h-6 text-gray-800" />
              </NavToggle>

              <MobileNavLinksList>
                {user ? (
                  <>
                    <MobileUserButton>{user.name}</MobileUserButton>
                    <MobileNavLink href="/" onClick={handleNavLinkClick}>{t('Acceuil')}</MobileNavLink>
                    <MobileNavLink href="/Boutique" onClick={handleNavLinkClick}>{t('Boutique')}</MobileNavLink>
                    <MobileNavLink href="/à-props" onClick={handleNavLinkClick}>{t('à_propos')}</MobileNavLink>
                    <MobileNavLink href="/contact" onClick={handleNavLinkClick}>{t('Contact')}</MobileNavLink>
                    <MobileNavLink href="/checkout" onClick={handleNavLinkClick}>
                      {t('Paiement')}
                    </MobileNavLink>
                    <MobileNavLink href="/dashboard" onClick={handleNavLinkClick}>{t('Tableau_de_bord')}</MobileNavLink>
                    <MobileNavLink href="/profile" onClick={handleNavLinkClick}>{t('Profile')}</MobileNavLink>
                    <LogoutButton onClick={() => { handleLogout(); toggleNavbar(); }}>
                      {t('Déconnexion')}
                    </LogoutButton>
                  </>
                ) : (
                  <>
                   <MobileNavLink href="/" onClick={handleNavLinkClick}>{t('Acceuil')}</MobileNavLink>
                    <MobileNavLink href="/Boutique" onClick={handleNavLinkClick}>{t('Boutique')}</MobileNavLink>
                    <MobileNavLink href="/à-props" onClick={handleNavLinkClick}>{t('à_propos')}</MobileNavLink>
                    <MobileNavLink href="/contact" onClick={handleNavLinkClick}>{t('Contact')}</MobileNavLink>
                    <MobileNavLink href="/checkout" onClick={handleNavLinkClick}>
                       {t('Paiement')}
                    </MobileNavLink>
                    <MobileNavLink href="/login" onClick={handleNavLinkClick}>{t('Connexion')}</MobileNavLink>
                    <MobilePrimaryLink href="/signup" onClick={handleNavLinkClick}> {t('Inscription')}
</MobilePrimaryLink>
                  </>
                )}
              </MobileNavLinksList>
            </MobileNavLinks>
          )}
        </AnimatePresence>
      </MobileNavLinksContainer>
    </Header>
  );
};

// Breakpoint mappings
const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};